import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { HeatmapMetric } from './utils';

export type HeatmapActive = 'inactive' | HeatmapMetric;

export interface HeatmapState {
  /** 'inactive' = hidden; otherwise which metric the map shows */
  active: HeatmapActive;
  /** Range requested from the API; only changing it (or the metric) triggers a fetch */
  baseRange: [number, number];
  /** Slot currently drawn on the map, half-open [start, end), inside baseRange */
  dateRange: [number, number];
  /** false: one colour scale for every slot of the range; true: rescale to the slot on screen */
  scaleToVisibleData: boolean;
  /** Bar/slot size of the chart; the map scale is computed over slots of this size */
  granularityMs: number;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  /** Key of the parsed dataset in `utils.datasetCache` that matches the current request */
  dataKey: string | null;
  /** Unique-vehicles series of the counter charts; parsed data lives in `utils.vehicleCache` */
  vehicleCounts: { status: 'idle' | 'loading' | 'ready' | 'error'; key: string | null };
}

export interface VehicleCountsRequest {
  start: number;
  end: number;
}

export interface HeatmapRequest {
  metric: HeatmapMetric;
  start: number;
  end: number;
}

const initialState: HeatmapState = {
  active: 'inactive',
  baseRange: [0, 0],
  dateRange: [0, 0],
  scaleToVisibleData: false,
  granularityMs: 60 * 60 * 1000,
  status: 'idle',
  error: null,
  dataKey: null,
  vehicleCounts: { status: 'idle', key: null },
};

// `heatmapRequest` is also a case saga (same name in sagas.ts): one dispatch sets the loading state
// and starts the fetch, see the two-namespace note in CLAUDE.md.
const heatmapReducer = createSlice({
  name: 'heatmap',
  initialState,
  reducers: {
    heatmapRequest(state, _action: PayloadAction<HeatmapRequest>) {
      state.status = 'loading';
      state.error = null;
    },
    heatmapLoaded(state, action: PayloadAction<string>) {
      state.status = 'ready';
      state.dataKey = action.payload;
    },
    heatmapFailed(state, action: PayloadAction<string>) {
      state.status = 'error';
      state.error = action.payload;
      state.dataKey = null;
    },
    // Also a case saga, like heatmapRequest
    vehicleCountsRequest(state, _action: PayloadAction<VehicleCountsRequest>) {
      state.vehicleCounts = { status: 'loading', key: null };
    },
    vehicleCountsLoaded(state, action: PayloadAction<string>) {
      state.vehicleCounts = { status: 'ready', key: action.payload };
    },
    vehicleCountsFailed(state) {
      state.vehicleCounts = { status: 'error', key: null };
    },
    setHeatmapActive(state, action: PayloadAction<HeatmapActive>) {
      state.active = action.payload;
    },
    setHeatmapBaseRange(state, action: PayloadAction<[number, number]>) {
      state.baseRange = action.payload;
    },
    setHeatmapDateRange(state, action: PayloadAction<[number, number]>) {
      state.dateRange = action.payload;
    },
    setHeatmapGranularity(state, action: PayloadAction<number>) {
      state.granularityMs = action.payload;
    },
    setHeatmapScaleToVisibleData(state, action: PayloadAction<boolean>) {
      state.scaleToVisibleData = action.payload;
    },
  },
});

export const {
  setHeatmapActive: setHeatmapActiveAction,
  setHeatmapBaseRange: setHeatmapBaseRangeAction,
  setHeatmapDateRange: setHeatmapDateRangeAction,
  setHeatmapGranularity: setHeatmapGranularityAction,
  setHeatmapScaleToVisibleData: setHeatmapScaleToVisibleDataAction,
  vehicleCountsRequest: vehicleCountsRequestAction,
} = heatmapReducer.actions;

export default heatmapReducer;
